import React, { useState } from 'react'
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts'
import { BarChart3, PieChart as PieChartIcon, AreaChart as AreaChartIcon, LineChart as LineChartIcon } from 'lucide-react'
import { formatCurrency, formatCompactNumber, formatDate, generateColors } from '../utils/helpers'
import { formatChartData, getDisplayName } from '../utils/dataMapping'
import Skeleton from 'react-loading-skeleton'

const ChartSkeleton = ({ height = 300 }) => (
  <div className="card">
    <div className="mb-3">
      <div className="skeleton h-5 w-40 rounded"></div>
    </div>
    <div className="skeleton rounded-lg" style={{ height }}></div>
  </div>
)

const CustomTooltip = ({ active, payload, label, formatValue = formatCurrency }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ backgroundColor: 'var(--chart-tooltip-bg)', border: '1px solid var(--chart-tooltip-border)', color: 'var(--chart-tooltip-text)' }} className="p-3 rounded-lg shadow-lg">
        <p className="font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            <span className="font-medium">{entry.name}:</span> {formatValue(entry.value)}
          </p>
        ))}
      </div>
    )
  }
  return null
}

const TimeSeriesChart = ({ data, loading = false }) => {
  const [chartType, setChartType] = useState('area') // 'line', 'area', 'bar'

  if (loading) {
    return <ChartSkeleton height={350} />
  }

  if (!data || !data.data || data.data.length === 0) {
    return (
      <div className="card">
        <h3 className="section-title mb-4 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-primary-500" />
          Évolution temporelle
        </h3>
        <div className="flex items-center justify-center h-48 text-sm text-gray-400 dark:text-gray-500">
          Aucune donnée temporelle disponible
        </div>
      </div>
    )
  }

  const chartData = data.data.map(item => ({
    date: formatDate(item.date, 'short'),
    valeur: item.valeur_da,
    quantite: item.quantite,
    poids: item.poids,
    articles: item.articles
  }))

  const renderChart = () => {
    const commonProps = {
      data: chartData,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    }

    switch (chartType) {
      case 'line':
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
            <XAxis dataKey="date" tick={{ fontSize: 12, fill: 'var(--chart-tick)' }} />
            <YAxis tick={{ fontSize: 12, fill: 'var(--chart-tick)' }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="valeur" 
              stroke="#3b82f6" 
              strokeWidth={2}
              name="Valeur (DA)"
              dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
            />
          </LineChart>
        )
      case 'area':
        return (
          <AreaChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
            <XAxis dataKey="date" tick={{ fontSize: 12, fill: 'var(--chart-tick)' }} />
            <YAxis tick={{ fontSize: 12, fill: 'var(--chart-tick)' }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Area
              type="monotone"
              dataKey="valeur"
              stroke="#3b82f6"
              fill="#3b82f6"
              fillOpacity={0.1}
              strokeWidth={2}
              name="Valeur (DA)"
            />
          </AreaChart>
        )
      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
            <XAxis dataKey="date" tick={{ fontSize: 12, fill: 'var(--chart-tick)' }} />
            <YAxis tick={{ fontSize: 12, fill: 'var(--chart-tick)' }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="valeur" fill="#3b82f6" name="Valeur (DA)" />
          </BarChart>
        )
      default:
        return null
    }
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="section-title flex items-center gap-2 text-sm">
          <BarChart3 className="w-4 h-4 text-primary-500" />
          Évolution {data.periode === 'monthly' ? 'Mensuelle' : 'Quotidienne'}
        </h3>
        
        <div className="flex items-center space-x-0.5 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
          {[
            { key: 'area', icon: AreaChartIcon, label: 'Zone' },
            { key: 'line', icon: LineChartIcon, label: 'Ligne' },
            { key: 'bar', icon: BarChart3, label: 'Barres' },
          ].map(({ key, icon: BtnIcon, label }) => (
            <button
              key={key}
              onClick={() => setChartType(key)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
                chartType === key
                  ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
              title={label}
            >
              <BtnIcon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>
      </div>
      
      <div className="h-72 sm:h-80">
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  )
}

const TopChart = ({ data, title, dimension, loading = false, rankingBy = 'value' }) => {
  const [chartType, setChartType] = useState('bar')

  if (loading) {
    return <ChartSkeleton height={280} />
  }

  if (!data || !data.items || data.items.length === 0) {
    return (
      <div className="card">
        <h3 className="section-title mb-4 flex items-center gap-2 text-sm">
          <PieChartIcon className="w-4 h-4 text-primary-500" />
          {title}
        </h3>
        <div className="flex items-center justify-center h-48 text-sm text-gray-400 dark:text-gray-500">
          Aucune donnée disponible
        </div>
      </div>
    )
  }

  // Déterminer le type de données pour le mapping
  const getDataType = (title) => {
    if (title.includes('Produit')) return 'numero_modele'
    if (title.includes('Importateur')) return 'default' // Pas de masquage pour les raisons sociales
    if (title.includes('Fournisseur')) return 'default' // Nom du fournisseur sans masquage
    if (title.includes('Pays')) return 'pays_origine'
    return 'default'
  }

  const dataType = getDataType(title)
  const chartData = data.items.slice(0, 8).map(item => {
    const displayName = getDisplayName(item.nom, dataType)
    // Augmenter la longueur max pour meilleure lisibilité (25 au lieu de 15)
    return {
      nom: displayName.length > 25 ? displayName.substring(0, 22) + '...' : displayName,
      fullName: displayName,
      originalName: item.nom,
      valeur: item.valeur_da,
      quantite: item.quantite,
      pourcentage: item.pourcentage
    }
  })

  const colors = generateColors(chartData.length)

  const CustomBarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div style={{ backgroundColor: 'var(--chart-tooltip-bg)', border: '1px solid var(--chart-tooltip-border)', color: 'var(--chart-tooltip-text)' }} className="p-3 rounded-lg shadow-lg">
          <p className="font-medium mb-2">{data.fullName}</p>
          <p className="text-sm text-blue-500">
            <span className="font-medium">{rankingBy === 'quantity' ? 'Quantité:' : 'Valeur:'}</span> {rankingBy === 'quantity' ? formatCompactNumber(data.quantite) : formatCurrency(data.valeur)}
          </p>
          <p className="text-sm" style={{ color: 'var(--chart-tooltip-muted)' }}>
            <span className="font-medium">Part:</span> {data.pourcentage.toFixed(1)}%
          </p>
        </div>
      )
    }
    return null
  }

  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div style={{ backgroundColor: 'var(--chart-tooltip-bg)', border: '1px solid var(--chart-tooltip-border)', color: 'var(--chart-tooltip-text)' }} className="p-3 rounded-lg shadow-lg">
          <p className="font-medium mb-2">{data.fullName}</p>
          <p className="text-sm text-blue-500">
            <span className="font-medium">{rankingBy === 'quantity' ? 'Quantité:' : 'Valeur:'}</span> {rankingBy === 'quantity' ? formatCompactNumber(data.quantite) : formatCurrency(data.valeur)}
          </p>
          <p className="text-sm" style={{ color: 'var(--chart-tooltip-muted)' }}>
            <span className="font-medium">Part:</span> {data.pourcentage.toFixed(1)}%
          </p>
        </div>
      )
    }
    return null
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="section-title text-sm">
          {title} <span className="text-xs font-normal text-gray-400 dark:text-gray-500">({rankingBy === 'quantity' ? 'Qté' : 'Val.'})</span>
        </h3>
        
        <div className="flex items-center space-x-0.5 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
          {[
            { key: 'bar', icon: BarChart3, label: 'Barres' },
            { key: 'pie', icon: PieChartIcon, label: 'Camembert' },
          ].map(({ key, icon: BtnIcon, label }) => (
            <button
              key={key}
              onClick={() => setChartType(key)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
                chartType === key
                  ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
              title={label}
            >
              <BtnIcon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>
      </div>
      
      <div className="h-80 sm:h-96">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'bar' ? (
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 40, bottom: 100 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
              <XAxis 
                dataKey="nom" 
                tick={{ fontSize: 11, fill: 'var(--chart-tick)' }}
                angle={-45}
                textAnchor="end"
                height={100}
                interval={0}
              />
              <YAxis 
                tick={{ fontSize: 11, fill: 'var(--chart-tick)' }}
                tickFormatter={(value) => formatCompactNumber(value)}
              />
              <Tooltip content={<CustomBarTooltip />} />
              <Bar dataKey={rankingBy === 'quantity' ? 'quantite' : 'valeur'} fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          ) : (
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ nom, pourcentage }) => pourcentage > 5 ? nom : ''}
                outerRadius={120}
                fill="#8884d8"
                dataKey={rankingBy === 'quantity' ? 'quantite' : 'valeur'}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomPieTooltip />} />
            </PieChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  )
}

const Charts = ({ data, loading = false, rankingBy = 'value' }) => {
  if (loading) {
    return (
      <div className="space-y-6">
        <ChartSkeleton height={350} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartSkeleton height={300} />
          <ChartSkeleton height={300} />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Time Series Chart */}
      <TimeSeriesChart data={data?.timeseries} loading={loading} />
      <div className="space-y-4">
        {/* Top Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <TopChart 
            data={data?.top_produits} 
            title="Top Produits" 
            dimension="produits"
            loading={loading}
            rankingBy={rankingBy}
          />
          <TopChart 
            data={data?.top_importateurs} 
            title="Top Importateurs" 
            dimension="importateurs"
            loading={loading}
            rankingBy={rankingBy}
          />
          <TopChart 
            data={data?.top_pays} 
            title="Top Pays" 
            dimension="pays"
            loading={loading}
            rankingBy={rankingBy}
          />
          <TopChart 
            data={data?.top_fournisseurs} 
            title="Top Fournisseurs" 
            dimension="fournisseurs"
            loading={loading}
            rankingBy={rankingBy}
          />
        </div>
      </div>
    </div>
  )
}

export default Charts
